import { Chip, Stack } from "@mui/material";
import { type ColumnProps } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { type GuestRequest, type GuestRequestEditor } from './types'
import { type SelectOption, type SmartTableColumnType } from "../../../utils/types";
import { type FormFieldType } from '../../Form'
import { getPKfromSelf, renderDateTime } from '../../../utils/functions'

type Props = {
  name:
    | 'guestRequestTableColumns'
    | 'guestRequestFormFields'
    | 'guestRequestEditFormFields'
    | 'sharepointFormFields'
    | 'editSharepointFormFields'
    | 'partInfoDrawerFields'
    | 'productInfoDrawerFields'
} & (
  | guestRequestTableColumnsKwargs
  | guestRequestFormFieldsKwargs
  | guestRequestEditFormFieldsKwargs
  | SharepointFormFieldsKwargs
  | EditSharepointFormFieldsKwargs
  | PartInfoDrawerFieldsKwargs
  | ProductInfoDrawerFieldsKwargs
)

type guestRequestTableColumnsKwargs = {
  name: 'guestRequestTableColumns'
  newGuestRequests?: GuestRequest[]
}

type guestRequestFormFieldsKwargs = {
  name: 'guestRequestFormFields'
}

type guestRequestEditFormFieldsKwargs = {
  name: 'guestRequestEditFormFields'
}
type SharepointFormFieldsKwargs = {
  name: 'sharepointFormFields'
  feedbackFormOptions?: SelectOption[]
  onAddFeedbackFormOption?: () => void
  ArticleListComponent?: React.ReactElement
  ArticleDescriptionComponent?: React.ReactElement
  DurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}
type EditSharepointFormFieldsKwargs = {
  name: 'editSharepointFormFields'
  SharedRequestComponent?: React.ReactElement
  RemainingDurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}

type PartInfoDrawerFieldsKwargs = {
  name: 'partInfoDrawerFields'
}

type ProductInfoDrawerFieldsKwargs = {
  name: 'productInfoDrawerFields'
}

/* ------------------------ Manufacturing Process Options start ------------------------ */
export const manufacturingProcessOptions: SelectOption[] = [
  {
    value: 'injectionMoulding',
    label: 'Injection Moulding',
  },
  {
    value: 'dieCasting',
    label: 'Die Casting',
  },
  {
    value: 'vacuumCasting',
    label: 'Vacuum Casting',
  },
  {
    value: 'sheetMetal',
    label: 'Sheet Metal',
  },
  {
    value: '3dPrinting',
    label: '3D Printing',
  },
  {
    value: 'cnc',
    label: 'CNC Machining',
  },
]
/* ------------------------- Manufacturing Process Options end ------------------------- */

/* -------------------------- Material Options start -------------------------- */
export const materialOptions: Record<string, SelectOption[]> = {
  injectionMoulding: [
    {
      value: 'abs',
      label: 'ABS',
    },
    {
      value: 'acryl',
      label: 'Acryl',
    },
    {
      value: 'hdpe',
      label: 'HDPE',
    },
    {
      value: 'peek',
      label: 'PEEK',
    },
    {
      value: 'pa6',
      label: 'PA6',
    },
    {
      value: 'pa6_gf30',
      label: 'PA6 (GF30)',
    },
    {
      value: 'pa6_gb30',
      label: 'PA6 (GB30)',
    },
    {
      value: 'pa6_gf50',
      label: 'PA6 (GF50)',
    },
    {
      value: 'pa6_gb50',
      label: 'PA6 (GB50)',
    },
    {
      value: 'pa66',
      label: 'PA66',
    },
    {
      value: 'pa66_gf30',
      label: 'PA66 (GF30)',
    },
    {
      value: 'pa66_gb30',
      label: 'PA66 (GB30)',
    },
    {
      value: 'pa66_gf50',
      label: 'PA66 (GF50)',
    },
    {
      value: 'pa66_gb50',
      label: 'PA66 (GB50)',
    },
    {
      value: 'pc',
      label: 'PC',
    },
    {
      value: 'pei',
      label: 'PEI',
    },
    {
      value: 'pet',
      label: 'PET',
    },
    {
      value: 'pla',
      label: 'PLA',
    },
    {
      value: 'pp',
      label: 'PP',
    },
    {
      value: 'pom',
      label: 'POM',
    },
    {
      value: 'ppe_ps',
      label: 'PPE-PS',
    },
    {
      value: 'ppe',
      label: 'PPE',
    },
    {
      value: 'psu',
      label: 'PSU',
    },
    {
      value: 'pvc',
      label: 'PVC',
    },
    {
      value: 'tpe',
      label: 'TPE',
    },
    {
      value: 'tpv',
      label: 'TPV',
    },
    {
      value: 'silicone',
      label: 'Silicone',
    },
    {
      value: 'rubber',
      label: 'Rubber',
    },
  ],
  dieCasting: [
    {
      value: 'adc12',
      label: 'ADC12',
    },
    {
      value: 'a380',
      label: 'A380',
    },
    {
      value: 'zamak4',
      label: 'Zamak4',
    },
    {
      value: 'zamak7',
      label: 'Zamak7',
    },
    {
      value: 'zl0400',
      label: 'ZL0400',
    },
    {
      value: 'zl0410',
      label: 'ZL0410',
    },
    {
      value: 'zl0430',
      label: 'ZL0430',
    },
  ],
  vacuumCasting: [
    {
      value: 'abs',
      label: 'ABS',
    },
    {
      value: 'pp',
      label: 'PP',
    },
    {
      value: 'pc',
      label: 'PC',
    },
    {
      value: 'pmma',
      label: 'PMMA',
    },
    {
      value: 'rubber',
      label: 'Rubber',
    },
    {
      value: 'silicone',
      label: 'Silicone',
    },
  ],
  sheetMetal: [
    {
      value: 'alu2007',
      label: 'Alu 2007',
    },
    {
      value: 'alu5052',
      label: 'Alu 5052',
    },
    {
      value: 'alu5083',
      label: 'Alu 5083',
    },
    {
      value: 'alu5754',
      label: 'Alu 5754',
    },
    {
      value: 'alu6060',
      label: 'Alu 6060',
    },
    {
      value: 'alu6061',
      label: 'Alu 6061',
    },
    {
      value: 'alu6082',
      label: 'Alu 6082',
    },
    {
      value: '1.4301',
      label: '1.4301',
    },
    {
      value: '1.4404',
      label: '1.4404',
    },
    {
      value: 's235jr',
      label: 'S235JR',
    },
    {
      value: 's275jr',
      label: 'S275JR',
    },
    {
      value: 's235j2',
      label: 'S235J2',
    },
    {
      value: 'dc01',
      label: 'DC01',
    },
    {
      value: 'c45',
      label: 'C45',
    },
    {
      value: 's355j2g3',
      label: 'S355J2G3',
    },
    {
      value: 'c45e',
      label: 'C45E',
    },
    {
      value: '1.2379',
      label: '1.2379',
    },
    {
      value: '1.7218',
      label: '1.7218',
    },
  ],
  '3dPrinting': [
    {
      value: 'abs',
      label: 'ABS',
    },
    {
      value: 'pa12',
      label: 'PA12',
    },
    {
      value: 'asa',
      label: 'ASA',
    },
    {
      value: 'epu40',
      label: 'EPU40',
    },
    {
      value: 'tpu',
      label: 'TPU',
    },
    {
      value: 'peek',
      label: 'PEEK',
    },
    {
      value: 'pa11',
      label: 'PA11',
    },
    {
      value: 'pc',
      label: 'PC',
    },
    {
      value: 'petg',
      label: 'PETG',
    },
    {
      value: 'pla',
      label: 'PLA',
    },
    {
      value: 'pp',
      label: 'PP',
    },
    {
      value: 'abs_esd',
      label: 'ABS (ESD)',
    },
    {
      value: 'ultem1010',
      label: 'ULTEM 1010',
    },
  ],
  cnc: [
    {
      value: 'abs',
      label: 'ABS',
    },
    {
      value: 'acryl',
      label: 'Acryl',
    },
    {
      value: 'peel',
      label: 'PEEL',
    },
    {
      value: 'pc',
      label: 'PC',
    },
    {
      value: 'peek',
      label: 'PEEK',
    },
    {
      value: 'pa6',
      label: 'PA6',
    },
    {
      value: 'pa6_gf30',
      label: 'PA6 (GF30)',
    },
    {
      value: 'pa6_gb30',
      label: 'PA6 (GB30)',
    },
    {
      value: 'pa6_gf50',
      label: 'PA6 (GF50)',
    },
    {
      value: 'pa6_gb50',
      label: 'PA6 (GB50)',
    },
    {
      value: 'pa66',
      label: 'PA66',
    },
    {
      value: 'pa66_gf30',
      label: 'PA66 (GF30)',
    },
    {
      value: 'pa66_gb30',
      label: 'PA66 (GB30)',
    },
    {
      value: 'pa66_gf50',
      label: 'PA66 (GF50)',
    },
    {
      value: 'pa66_gb50',
      label: 'PA66 (GB50)',
    },
    {
      value: 'ptfe',
      label: 'PTFE',
    },
    {
      value: 'pom',
      label: 'POM',
    },
    {
      value: 'pp',
      label: 'PP',
    },
    {
      value: 'pvdf',
      label: 'PVDF',
    },
    {
      value: 'uhmw_pe',
      label: 'UHMW PE',
    },
    {
      value: 'alu2007',
      label: 'Alu 2007',
    },
    {
      value: 'alu5052',
      label: 'Alu 5052',
    },
    {
      value: 'alu5083',
      label: 'Alu 5083',
    },
    {
      value: 'alu5754',
      label: 'Alu 5754',
    },
    {
      value: 'alu6060',
      label: 'Alu 6060',
    },
    {
      value: 'alu6061',
      label: 'Alu 6061',
    },
    {
      value: 'alu6082',
      label: 'Alu 6082',
    },
    {
      value: 'alu7075',
      label: 'Alu 7075',
    },
    {
      value: '1.4301',
      label: '1.4301',
    },
    {
      value: '1.4404',
      label: '1.4404',
    },
    {
      value: 's235jr',
      label: 'S235JR',
    },
    {
      value: 's275jr',
      label: 'S275JR',
    },
    {
      value: 's235j2',
      label: 'S235J2',
    },
    {
      value: 'dc01',
      label: 'DC01',
    },
    {
      value: 'c45',
      label: 'C45',
    },
    {
      value: 'titanium',
      label: 'Titanium',
    },
    {
      value: 'brass',
      label: 'Brass',
    },
    {
      value: 'copper',
      label: 'Copper',
    },
  ],
}
/* --------------------------- Material Options end --------------------------- */

/* -------------------------- Treatment Options start -------------------------- */
export const treatmentOptions: SelectOption[] = [
  {
    value: 'burnishing',
    label: 'Burnishing',
  },
  {
    value: 'blasting',
    label: 'Sand and glass bead blasting',
  },
  {
    value: 'anodizing',
    label: 'Anodizing',
  },
  {
    value: 'chromating',
    label: 'Chromating',
  },
  {
    value: 'vibratoryFinishing',
    label: 'Vibratory Finishing',
  },
  {
    value: 'electropolishing',
    label: 'Electropolishing',
  },
  {
    value: 'phosphating',
    label: 'Phosphating',
  },
  {
    value: 'oxidizing',
    label: 'Oxidizing',
  },
  {
    value: 'hardening',
    label: 'Hardening',
  },
  {
    value: 'annealing',
    label: 'Stress-relief annealing',
  },
  {
    value: 'gasNitriding',
    label: 'Gas nitriding',
  },
  {
    value: 'plasmaNitriding',
    label: 'Plasma nitriding',
  },
  {
    value: 'galvanizing',
    label: 'Galvanizing',
  },
  {
    value: 'nickelPlating',
    label: 'Nickel plating',
  },
  {
    value: 'ultrasonicCleaning',
    label: 'Ultrasonic cleaning',
  },
  {
    value: 'powderCoating',
    label: 'Powder coating',
  },
  {
    value: 'varnishing',
    label: 'Varnishing',
  },
  {
    value: 'hotDipGalvanizing',
    label: 'Hot-dip galvanizing',
  },
]
/* --------------------------- Treatment Options end --------------------------- */

/* ------------------------ Quality Class Options start ------------------------ */
export const qualityClassOptions: SelectOption[] = [
  {
    value: 'fh',
    label: 'fh (Fine)',
  },
  {
    value: 'mk',
    label: 'mk (Medium)',
  },
  {
    value: 'cl',
    label: 'cl (Rough)',
  },
  {
    value: 'vl',
    label: 'vl (Very rough)',
  },
]
/* ------------------------- Quality Class Options end ------------------------- */

/* ------------------------ Utility functions start ------------------------ */
/**
 * Helper function to get material options based on manufacturing process
 * @param manufacturingProcess - Selected manufacturing process value
 * @returns Array of material options for the selected process
 */
export const getMaterialOptions = (
  manufacturingProcess: string | undefined
): SelectOption[] | undefined => {
  if (!manufacturingProcess || !materialOptions[manufacturingProcess]) return undefined
  return materialOptions[manufacturingProcess]
}
/* ------------------------- Utility functions end ------------------------- */

export default function GetConstant(props: Props): SmartTableColumnType<any>[] | FormFieldType[] | ColumnProps<any>[] {
  const { t: tHook } = useTranslation(['common', 'request'])
  // Use provided t function or fallback to hook for sharepoint form fields
  const t =
    props.name === 'sharepointFormFields' || props.name === 'editSharepointFormFields'
      ? ((props as SharepointFormFieldsKwargs | EditSharepointFormFieldsKwargs).t || tHook)
      : tHook

  if (props.name === 'guestRequestTableColumns') {
    return [
      {
        title: t('request:content.label.email'),
        dataIndex: 'email',
        key: 'email',
        render: (email: string | null | undefined, record: GuestRequest) => {
          if(!email) return '-'
          if(record.read) return email
          // the user has not seen the GuestRequest yet -> render 'new' badge
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                color='primary'
                label={t('common:content.label.new')}
                size='small'
                sx={{ color: 'whitesmoke' }}
              />
              <span>{email}</span>
            </Stack>
          )
        },
        width: '300px',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'email',
          groupBy: {
            key: 'contactInformation',
            label: t('common:content.label.contactInfo')
          }
        },
      },
      {
        title: t('request:content.label.requestNumber'),
        dataIndex: 'self',
        key: 'self',
        render: (self: string) => getPKfromSelf(self),
        sorter: true,
        filter: {
          inputType: 'number',
          filterKey: 'request_number',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('request:content.label.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: any) => name ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'name',
          groupBy: {
            key: 'contactInformation',
            label: t('common:content.label.contactInfo')
          }
        },
      },
      {
        title: t('request:content.label.company'),
        dataIndex: 'company',
        key: 'company',
        render: (company: any) => company ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'company',
          groupBy: {
            key: 'contactInformation',
            label: t('common:content.label.contactInfo')
          }
        },
      },
      {
        title: t('request:content.label.phone'),
        dataIndex: 'phone',
        key: 'phone',
        render: (phone: any) => phone ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'phone',
          groupBy: {
            key: 'contactInformation',
            label: t('common:content.label.contactInfo')
          }
        },
      },
      {
        title: t('request:content.label.quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity: any) => quantity ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'quantity',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('common:content.label.material'),
        dataIndex: 'material',
        key: 'material',
        render: (material: string | null | undefined) => material ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'material',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('request:content.label.description'),
        dataIndex: 'message',
        key: 'description',
        render: (message: any) => message ?? '-',
        width: '25%',
        ellipsis: true,
        filter: {
          inputType: 'text',
          filterKey: 'description',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('common:content.label.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string | null | undefined) => {
          if(!status) return '-'
          return (
            <Chip
              label={status}
              color={
                (status==='pending' && 'warning') ||
                (status==='accepted' && 'success') ||
                'info'
              }
              variant='outlined'
            />
          )
        },
        sorter: (a: GuestRequest, b: GuestRequest) => {
          // check for undefined and null values
          if(!a.status && b.status) return -1
          if(a.status && !b.status) return 1
          if(!a.status && !b.status) return 0

          // type check to make TypeScript happy
          if(a.status !== undefined && a.status !== null
            && b.status !== undefined && b.status !== null
          ) {
            const statusOrder: Record<string, number> = {
              pending: 1,
              accepted: 2,
              rejected: 3,
            }
            return statusOrder[a.status] - statusOrder[b.status]
          }
        },
        filter: {
          inputType: 'text',
          filterKey: 'status',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('common:content.label.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (createdAt: any) => renderDateTime(createdAt, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'created_at',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('common:content.label.editors'),
        key: 'editors',
        dataIndex: 'editors',
        render: (text: string, record: GuestRequest) => {
          if (Array.isArray(record.editors)) {
            return (
              <>
                {record.editors.map((editor: GuestRequestEditor | string) => {
                  if (typeof editor === 'string') return null

                  const fullName = [editor.firstName, editor.lastName]
                    .filter(Boolean)
                    .join(' ')

                  return (
                    <Chip
                      key={editor.self}
                      label={fullName || editor.self}
                      style={{ marginRight: '5px' }}
                    />
                  )
                })}
              </>
            )
          }
          return ''
        },
        filter: {
          inputType: 'select',
          multiple: true,
          filterKey: 'editors',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.guestRequestInformation')
          }
        },
      },
      {
        title: t('common:content.label.remarks'),
        dataIndex: 'remarks',
        key: 'remarks',
        render: (remarks: string | null | undefined) => remarks || '-',
        width: '25%',
        ellipsis: true,
      },
    ] as SmartTableColumnType<GuestRequest>[]
  }
  if (props.name === 'guestRequestFormFields') {
    return [
      // Contact Information Group
      {
        type: 'text',
        key: 'name',
        label: t('request:content.label.name'),
        required: true,
        validation: true,
        showError: false,
        errorMessage: t('request:feedback.error.shouldNotBeEmpty'),
        xl: 6,
        lg: 6,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'email',
        key: 'email',
        label: t('request:content.label.email'),
        required: true,
        validation: true,
        showError: false,
        xl: 6,
        lg: 6,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'text',
        key: 'company',
        label: t('request:content.label.company'),
        required: true,
        validation: true,
        showError: false,
        xl: 6,
        lg: 6,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'text',
        key: 'phone',
        label: t('request:content.label.phone'),
        required: false,
        validation: true,
        showError: false,
        xl: 6,
        lg: 6,
        md: 12,
        sm: 12,
        xs: 12,
      },
      // Request Details Group
      {
        type: 'quantity-multi',
        key: 'quantity',
        label: t('request:content.label.quantity'),
        required: true,
        validation: true,
        showError: true,
        errorMessage: t('request:feedback.error.shouldNotBeEmpty'),
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'text',
        key: 'material',
        label: t('common:content.label.material'),
        required: true,
        validation: true,
        showError: false,
        errorMessage: t('request:feedback.error.shouldNotBeEmpty'),
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'multiline',
        key: 'message',
        label: t('request:content.label.description'),
        required: false,
        validation: false,
        showError: false,
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
    ] as FormFieldType[]
  }
  if (props.name === 'guestRequestEditFormFields') {
    return [
      {
        type: 'text',
        key: 'status',
        label: t('common:content.label.status'),
        required: false,
        validation: false,
        showError: false,
        suggestions: [
          { value: 'pending', label: t('common:content.label.pending') },
          { value: 'approved', label: t('common:content.label.approved') },
          { value: 'rejected', label: t('common:content.label.rejected') },
        ],
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'select-search-multi',
        key: 'editors',
        label: t('common:content.label.editors'),
        autocompleteField: 'staff-users',
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'multiline',
        key: 'remarks',
        label: t('common:content.label.remarks'),
        required: false,
        validation: false,
        showError: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'sharepointFormFields') {
    const sharepointProps = props as SharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'articleList',
        label: t('common:content.label.article'),
        CustomComponent: sharepointProps.ArticleListComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'articleDescription',
        label: '',
        CustomComponent: sharepointProps.ArticleDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'sharepointDuration',
        label: t('sharepoint:content.label.sharepointDuration'),
        CustomComponent: sharepointProps.DurationInputComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        label: '',
        CustomComponent: sharepointProps.DurationDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'select-search',
        key: 'feedbackForm',
        label: t('sharepoint:content.label.feedbackForm'),
        options: sharepointProps.feedbackFormOptions,
        onAddOption: sharepointProps.onAddFeedbackFormOption,
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'editSharepointFormFields') {
    const editSharepointProps = props as EditSharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'sharedRequest',
        CustomComponent: editSharepointProps.SharedRequestComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'remainingSharepointDuration',
        CustomComponent: editSharepointProps.RemainingDurationInputComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        CustomComponent: editSharepointProps.DurationDescriptionComponent,
        xs: 4,
        sm: 4,
        md: 4,
        lg: 4,
        xl: 4,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'partInfoDrawerFields') {
    const partProps = props as PartInfoDrawerFieldsKwargs
    return [
      {
        type: 'number',
        key: 'quantity',
        label: t('common:content.label.quantity'),
        required: true,
        validation: true,
        showError: true,
        errorMessage: t('request:feedback.error.invalidQuantityRange'),
        limits: { minInput: 1, maxInput: 100000 },
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'selectWithFreeText',
        key: 'manufacturing_process',
        label: t('common:content.label.manufacturingProcess'),
        required: true,
        validation: true,
        showError: false,
        options: manufacturingProcessOptions,
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'selectWithFreeText',
        key: 'material',
        label: t('common:content.label.material'),
        required: true,
        validation: true,
        showError: false,
        // options are dynamically injected in the drawer based on manufacturing process
        options: [],
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'select-search-multi',
        key: 'treatments',
        label: t('item:content.label.post_treatment'),
        required: false,
        validation: false,
        showError: false,
        options: treatmentOptions,
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
      {
        type: 'selectWithFreeText',
        key: 'qualityClass',
        label: t('common:content.label.qualityClass'),
        required: false,
        validation: false,
        showError: false,
        options: qualityClassOptions,
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'productInfoDrawerFields') {
    const productProps = props as ProductInfoDrawerFieldsKwargs
    return [
      {
        type: 'number',
        key: 'quantity',
        label: t('request:content.label.orderQuantity'),
        required: true,
        validation: true,
        showError: true,
        errorMessage: t('request:feedback.error.invalidQuantityRange'),
        limits: { minInput: 1, maxInput: 100000 },
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
    ] as FormFieldType[]
  }

  return [] as FormFieldType[] | ColumnProps<any>[]
}
